/**
 * HelpDrawer — In-app User Guide
 *
 * Slide-in right-side drawer with searchable accordion sections.
 * Static content — no API call needed.
 * Close with Escape key, backdrop click, or the × button.
 */
import React, { useState, useEffect, useRef } from "react";

// ─── Content ──────────────────────────────────────────────────────────────────

interface HelpSection {
  id: string;
  title: string;
  icon: string;
  content: React.ReactElement;
}

const HELP_SECTIONS: HelpSection[] = [
  {
    id: "getting_started",
    title: "Getting Started",
    icon: "🚀",
    content: (
      <div>
        <p style={{ margin: "0 0 10px" }}>The institution app lets you initiate and govern collateral recovery cases. Here's the basic flow:</p>
        <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 2 }}>
          <li>Click <strong>+ Initiate Case</strong> in the Case Queue.</li>
          <li><strong>Phase 1 — Fetch Collateral Registry data:</strong> Enter the loan reference number from your core banking system. In development, use a simulation reference from the <em>Simulation Mode (Development)</em> section below.</li>
          <li><strong>Phase 2 — Review data:</strong> Check loan details, borrower info, and asset info across 5 tabs. Add auction parameters and an optional memo.</li>
          <li><strong>Phase 3 — Submit:</strong> Select a workflow template and click <em>Initiate Recovery Case</em>. The system creates the case and immediately dispatches field agents.</li>
        </ol>
      </div>
    ),
  },
  {
    id: "workflow",
    title: "Case Workflow — 18 Steps",
    icon: "📋",
    content: (
      <div>
        <p style={{ margin: "0 0 12px", color: "var(--muted)", fontSize: 13 }}>The standard recovery workflow has 18 steps. Steps marked ⚡ require your action.</p>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid var(--border)" }}>
              <th style={{ padding: "6px 8px", textAlign: "left", color: "var(--muted)", fontWeight: 700 }}>#</th>
              <th style={{ padding: "6px 8px", textAlign: "left", color: "var(--muted)", fontWeight: 700 }}>Step</th>
              <th style={{ padding: "6px 8px", textAlign: "left", color: "var(--muted)", fontWeight: 700 }}>Owner</th>
            </tr>
          </thead>
          <tbody>
            {[
              [1, "Case Initiation", "Institution ⚡"],
              [2, "Field Seller Dispatch", "System"],
              [3, "Field Verification", "Field DAA"],
              [4, "Hub Dispatch", "System"],
              [5, "Hub Witness", "Hub Ops"],
              [6, "Hub Processing", "Hub Ops"],
              [7, "Expert Dispatch", "System"],
              [8, "Expert Valuation", "Expert DAA"],
              [9, "Cataloging", "Field DAA"],
              [10, "Auction Approval", "Institution ⚡"],
              [11, "Market Listing", "Marketplace"],
              [12, "Hub Inspection", "Hub Ops"],
              [13, "Bidding Approval", "Institution ⚡"],
              [14, "Live Bidding", "Marketplace"],
              [15, "Auction Closed", "Marketplace"],
              [16, "Winner Approval", "Institution ⚡"],
              [17, "Winner Posted", "Marketplace"],
              [18, "Settlement", "Institution ⚡"],
            ].map(([n, label, owner]) => (
              <tr key={String(n)} style={{ borderBottom: "1px solid var(--border)" }}>
                <td style={{ padding: "5px 8px", color: "var(--muted)" }}>{n}</td>
                <td style={{ padding: "5px 8px", fontWeight: 600, color: "var(--ink)" }}>{label}</td>
                <td style={{ padding: "5px 8px", color: String(owner).includes("⚡") ? "var(--brand)" : "var(--muted)" }}>{owner}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
  },
  {
    id: "approval_gates",
    title: "Approval Gates (Steps 10, 13, 16, 18)",
    icon: "⚡",
    content: (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {[
          { step: "Step 10 — Auction Approval", desc: "Review the cataloged asset data submitted by DAAs and the expert valuation. Verify the auction parameters (price, timing, bid security). Click 'Approve Listing' to hand the case off to the marketplace." },
          { step: "Step 13 — Bidding Approval", desc: "Review the live listing on the marketplace and confirm it is correctly set up. Click 'Authorize Bidding' to open the auction to buyers." },
          { step: "Step 16 — Winner Approval", desc: "Review the auction winner's bid and verify their identity. Click 'Approve Winner' to notify the marketplace and allow settlement to proceed." },
          { step: "Step 18 — Settlement", desc: "Record the final settlement amount and confirm receipt of funds. This is the commitment boundary — once submitted the case is permanently closed and a case rollup is generated." },
        ].map(({ step, desc }) => (
          <div key={step} style={{ background: "var(--bg)", border: "1px solid var(--border)", borderLeft: "3px solid var(--brand)", borderRadius: "var(--radius-sm)", padding: "10px 14px" }}>
            <p style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 800, color: "var(--ink)" }}>{step}</p>
            <p style={{ margin: 0, fontSize: 12, color: "var(--muted)", lineHeight: 1.6 }}>{desc}</p>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: "metrics",
    title: "Metrics Dashboard Glossary",
    icon: "📊",
    content: (
      <div>
        {[
          ["Total Cases", "All cases ever created in your institution's portfolio."],
          ["Active Cases", "Cases that are open (not closed) and progressing through the workflow."],
          ["Recovered", "Cases that have completed Step 18 (Settlement) and been closed."],
          ["Recovered (30d)", "Cases recovered in the last 30 calendar days."],
          ["Stalled", "Active cases where the current step has been waiting longer than the SLA limit (5 min in dev, longer in production)."],
          ["Recovery Rate", "Percentage of all cases (active + closed) that have been successfully recovered."],
          ["Stage Pipeline", "Shows how many active cases are in each broad stage of the workflow."],
          ["Step Distribution", "Shows how many cases (including closed ones) passed through each of the 18 steps."],
        ].map(([term, def]) => (
          <div key={term} style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>{term}</p>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--muted)" }}>{def}</p>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: "simulation",
    title: "Simulation Mode (Development)",
    icon: "🧪",
    content: (
      <div>
        <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--muted)" }}>When no live Collateral Registry bank API is configured, the system runs in simulation mode. Use these test loan references:</p>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid var(--border)" }}>
              <th style={{ padding: "5px 8px", textAlign: "left", color: "var(--muted)", fontWeight: 700 }}>Loan Ref</th>
              <th style={{ padding: "5px 8px", textAlign: "left", color: "var(--muted)", fontWeight: 700 }}>Asset</th>
              <th style={{ padding: "5px 8px", textAlign: "left", color: "var(--muted)", fontWeight: 700 }}>Zone</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["FAST-SIM-001", "Toyota Land Cruiser", "Addis Ababa"],
              ["FAST-SIM-002", "Commercial Building", "Addis Ababa"],
              ["FAST-SIM-003", "Agricultural Land", "Amhara"],
              ["FAST-SIM-004", "Industrial Equipment", "Oromia"],
              ["FAST-SIM-005", "Pickup Truck", "SNNPR"],
              ["FAST-SIM-006", "Residential Plot", "Tigray"],
              ["FAST-SIM-007", "Coffee Processing Plant", "Sidama"],
              ["FAST-SIM-008", "Minibus", "Dire Dawa"],
              ["FAST-SIM-009", "Office Building", "Harari"],
              ["FAST-SIM-010", "Heavy Machinery", "Oromia"],
              ["FAST-SIM-011", "Warehouse", "Afar"],
              ["FAST-SIM-012", "Suzuki Swift", "Addis Ababa"],
            ].map(([ref, asset, zone]) => (
              <tr key={ref} style={{ borderBottom: "1px solid var(--border)" }}>
                <td style={{ padding: "5px 8px", fontFamily: "monospace", color: "var(--brand)" }}>{ref}</td>
                <td style={{ padding: "5px 8px", color: "var(--ink)" }}>{asset}</td>
                <td style={{ padding: "5px 8px", color: "var(--muted)" }}>{zone}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ margin: "10px 0 0", fontSize: 11, color: "var(--muted)" }}>You can also click "Browse Simulation Records" in the Initiate Case page to see the full catalogue with amounts and borrower details.</p>
      </div>
    ),
  },
  {
    id: "faq",
    title: "Common Issues & FAQs",
    icon: "❓",
    content: (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {[
          { q: "Why is my case stuck at Step 2 (Field Dispatch)?", a: "A dispatch was broadcast to eligible Field Seller DAAs. If no DAA accepts within the SLA time limit, the dispatch will timeout and the step will be auto-advanced by the SLA worker. Check the Metrics Dashboard for stalled cases." },
          { q: "What does 'auto-completed' mean in the audit log?", a: "When a step exceeds its SLA time limit, the system's SLA worker automatically completes it to keep the pipeline moving. This only happens in development mode. In production, SLA violations are surfaced as alerts and require human review." },
          { q: "Can I edit auction parameters after submitting?", a: "Yes, but only before Step 10 Auction Approval is completed. Once approved, the auction parameters are locked as a commitment boundary." },
          { q: "How do I add more notes to a case?", a: "Open the case → Memo tab → type your note and click 'Add Memo'. Memos are append-only and cannot be deleted." },
          { q: "The Collateral Registry fetch returned an error. What should I do?", a: "Check that the loan reference number is correct. If Collateral Registry is unavailable, use a simulation reference (FAST-SIM-001 through FAST-SIM-012) to continue testing the workflow." },
        ].map(({ q, a }) => (
          <div key={q} style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", padding: "10px 14px" }}>
            <p style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>Q: {q}</p>
            <p style={{ margin: 0, fontSize: 12, color: "var(--muted)", lineHeight: 1.6 }}>A: {a}</p>
          </div>
        ))}
      </div>
    ),
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

interface HelpDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function HelpDrawer({ open, onClose }: HelpDrawerProps): React.ReactElement {
  const [expandedId, setExpandedId] = useState<string | null>("getting_started");
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Auto-focus search when opened
  useEffect(() => {
    if (open && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [open]);

  const q = search.toLowerCase().trim();
  const filtered = q
    ? HELP_SECTIONS.filter((s) => s.title.toLowerCase().includes(q) || String(s.content).toLowerCase().includes(q))
    : HELP_SECTIONS;

  if (!open) return <></>;

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)", zIndex: 199 }} />

      {/* Drawer */}
      <div
        role="dialog"
        aria-label="User Guide"
        aria-modal="true"
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0, width: 440,
          background: "var(--panel)", zIndex: 200,
          display: "flex", flexDirection: "column",
          boxShadow: "-8px 0 40px rgba(15,23,42,0.18)",
          animation: "slideInRight 0.22s ease",
        }}
      >
        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 22 }}>📖</span>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 16, fontWeight: 900, color: "var(--ink)" }}>User Guide</p>
            <p style={{ margin: 0, fontSize: 12, color: "var(--muted)" }}>Institution Portal</p>
          </div>
          <button onClick={onClose} aria-label="Close help" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "var(--muted)", padding: 4 }}>✕</button>
        </div>

        {/* Search */}
        <div style={{ padding: "12px 24px", borderBottom: "1px solid var(--border)" }}>
          <input
            ref={searchRef}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search guide…"
            aria-label="Search help sections"
            style={{ width: "100%", padding: "8px 12px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", background: "var(--bg)", color: "var(--ink)", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }}
          />
        </div>

        {/* Sections */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
          {filtered.length === 0 && (
            <p style={{ padding: "20px 24px", color: "var(--muted)", fontSize: 14 }}>No sections match "{search}".</p>
          )}
          {filtered.map((section) => {
            const isOpen = expandedId === section.id;
            return (
              <div key={section.id} style={{ borderBottom: "1px solid var(--border)" }}>
                <button
                  onClick={() => setExpandedId(isOpen ? null : section.id)}
                  aria-expanded={isOpen}
                  style={{
                    width: "100%", padding: "14px 24px", background: "none", border: "none", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 10, textAlign: "left",
                  }}
                >
                  <span style={{ fontSize: 18, flexShrink: 0 }}>{section.icon}</span>
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>{section.title}</span>
                  <span style={{ fontSize: 12, color: "var(--muted)" }}>{isOpen ? "▲" : "▼"}</span>
                </button>
                {isOpen && (
                  <div style={{ padding: "0 24px 20px", fontSize: 13, color: "var(--ink)", lineHeight: 1.7 }}>
                    {section.content}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 24px", borderTop: "1px solid var(--border)", textAlign: "center" }}>
          <p style={{ margin: 0, fontSize: 11, color: "var(--muted)" }}>
            AEOS Institution Portal · For support, contact your system administrator.
          </p>
        </div>
      </div>

      <style>{`@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </>
  );
}
